import { useMemo } from 'react';
import { useParams } from '@tanstack/react-router';
import { useProject, useProjectActivity, useWorkspace } from '@fluxter/shared';
import { ProjectPageHeader } from '@/components/ProjectPageHeader';
import { ProjectViewSwitcher } from '@/components/ProjectViewSwitcher';
import { ActivityFeed } from '@/components/ActivityFeed';
import type { ActivityEntry } from '@/lib/format-activity';
import { useSetTopBarSlots } from '@/components/TopBarSlots';
import { SkeletonList } from '@/components/Skeletons';

interface ActivityListPayload {
  data?: ReadonlyArray<ActivityEntry>;
}

/**
 * Project-scoped activity feed. Mirrors the `ActivityFeed` rendered on
 * `WorkspaceHomePage` (workspace-wide) and `TaskDetailPage` (per-task)
 * but filtered to events touching this project: task creation /
 * status changes / reorder, comments on tasks within the project,
 * note creation, mentions, etc.
 *
 * Currently a single-page fetch (server returns up to 50 entries —
 * the `useProjectActivity` hook is a plain `useQuery`, not infinite).
 * If a busy project ever overflows the window, upgrade the hook to
 * `useInfiniteQuery` and add a Load-more button here, mirroring the
 * `useWorkspaceActivity` pattern. For v1 project sizes, 50 entries
 * comfortably covers the recent-activity question.
 *
 * Page-adding contract reference: see `app/PRODUCT.md` § 9. This page
 * follows it exactly — eager-imported in the router, no
 * `<WorkspaceShell>` wrapper, loader-driven cache warming.
 */
export const ProjectActivityPage = () => {
  const { workspaceSlug, projectSlug } = useParams({
    from: '/w/$workspaceSlug/p/$projectSlug/activity',
  });
  const workspace = useWorkspace(workspaceSlug);
  const project = useProject(workspaceSlug, projectSlug);
  const activity = useProjectActivity(workspaceSlug, projectSlug);

  const workspaceName = (workspace.data as { data?: { attributes?: { name?: string } } } | undefined)
    ?.data?.attributes?.name;
  const projectName = (project.data as { data?: { attributes?: { name?: string } } } | undefined)
    ?.data?.attributes?.name;

  const entries = (activity.data as ActivityListPayload | undefined)?.data ?? [];

  // Topbar contribution — ViewSwitcher in the right slot, same as
  // every other project surface. No `cta` slot here: activity is a
  // read-only feed, no New-X action makes sense.
  const topBarRight = useMemo(
    () => (
      <ProjectViewSwitcher
        workspaceSlug={workspaceSlug}
        projectSlug={projectSlug}
        current="activity"
      />
    ),
    [workspaceSlug, projectSlug],
  );
  useSetTopBarSlots({ right: topBarRight });

  return (
    <>
      <div className="space-y-6">
        <ProjectPageHeader
          workspaceSlug={workspaceSlug}
          workspaceName={workspaceName}
          projectSlug={projectSlug}
          projectName={projectName}
          current="activity"
        />

        <header>
          <h1 className="text-foreground text-[28px] font-semibold tracking-[-0.025em]">
            Activity
          </h1>
          <p className="text-foreground-faint mt-1 text-[12.5px]">
            Recent events across this project — task changes, comments, mentions, and notes.
            Workspace-wide activity lives on the workspace home; per-task feeds live on each
            task's detail page.
          </p>
        </header>

        {activity.isLoading ? (
          <SkeletonList rows={6} withAvatar />
        ) : (
          // `compact` strips ActivityFeed's own "Activity" sub-header
          // — the page h1 already provides that label.
          <ActivityFeed
            workspaceSlug={workspaceSlug}
            entries={entries}
            emptyLabel="No activity in this project yet."
            compact
          />
        )}
      </div>
    </>
  );
};
